/**
 * Deck-language helpers (WCAG 3.1.1 Language of Page).
 *
 * The language is stored as the deck-level `language:` directive, auto-filled
 * from the author's browser at creation and validated/canonicalised so the
 * viewer can safely apply it to the `lang` attribute. A malformed value can
 * never reach `lang` — it falls back to English.
 */

export const DEFAULT_LANGUAGE = "en";

/**
 * Validate + canonicalise a BCP-47 language tag, falling back to
 * DEFAULT_LANGUAGE. Uses the platform's own Intl validation (available in the
 * browser and the Workers/SSR runtime) rather than a hand-maintained list:
 * `Intl.getCanonicalLocales` throws on a malformed tag and canonicalises a
 * good one ("EN_us" -> "en-US").
 */
export function normalizeLanguageTag(value: string | undefined | null): string {
	const raw = value?.trim();
	if (!raw) return DEFAULT_LANGUAGE;
	// getCanonicalLocales exists at runtime (browser + Workers/SSR) but isn't in
	// this project's TS lib, so reach it through a narrow cast.
	const getCanonicalLocales = (
		Intl as unknown as {
			getCanonicalLocales?: (tags: string | string[]) => string[];
		}
	).getCanonicalLocales;
	if (!getCanonicalLocales) return raw; // env without Intl — pass the raw tag through
	try {
		const [canonical] = getCanonicalLocales(raw);
		return canonical || DEFAULT_LANGUAGE;
	} catch {
		return DEFAULT_LANGUAGE;
	}
}

/**
 * The author's browser language, normalised — the auto-fill default written
 * into a new deck's `language:` directive so authors never have to type it.
 */
export function detectBrowserLanguage(): string {
	const nav = typeof navigator !== "undefined" ? navigator.language : undefined;
	return normalizeLanguageTag(nav);
}
